import React, { useState, useEffect } from "react";
import {
  db,
  onSnapshot,
  collection,
  doc,
  where,
  query,
  limit,
  getDoc,
  updateDoc,
} from "../../firebaseConfig";
import { useAtom } from "jotai";
import {
  themeAtom,
  isAdminAtom,
  isMobileAtom,
  eventDataModalTabValueAtom,
  isShootTabAtom,
  emailJobDataAtom,
  emailClientDataAtom,
  emailSelectorDialogOpenAtom,
  activeMultiDayIdAtom,
} from "../../-Atoms";
import { motion, AnimatePresence } from "framer-motion";
import "../Event Data Modal Components/style.css";
import CloseButton from "./components/CloseButton";
import MenuButton from "./components/MenuButton";
import PreProdTab from "./components/PreProdTab";
import ClientTab from "./components/ClientTab";
import JobTab from "./components/JobTab";
import EmailSelectorDialogV2 from "./components/EmailSelectorDialogV2";
import HistoryDialog from "./components/HistoryDialog";

import JobTitle from "./components/JobTitle";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";

const EventDataModalV2 = ({
  initialJobData,
  eventDataModalOpen,
  setEventDataModalOpen,
  isEventTomorrow,
  confirmationStatusEmailOpen,
  setConfirmationStatusEmailOpen,
  firstConfirmationStatusEmailOpen,
  setFirstConfirmationStatusEmailOpen,
  setConfirmationEmailData,
  clientOnlyMode = false,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [isMobile] = useAtom(isMobileAtom);
  const [eventDataModalTabValue] = useAtom(eventDataModalTabValueAtom);
  const [, setIsShootTab] = useAtom(isShootTabAtom);
  const [, setEmailJobData] = useAtom(emailJobDataAtom);
  const [, setEmailClientData] = useAtom(emailClientDataAtom);
  const [emailSelectorDialogOpen, setEmailSelectorDialogOpen] = useAtom(
    emailSelectorDialogOpenAtom
  );
  const [jobData, setJobData] = useState({});
  const [jobClientData, setJobClientData] = useState(null);
  const [allMultipleDayJobs, setAllMultipleDayJobs] = useState([]);
  const [activeMultiDayId, setActiveMultiDayId] = useAtom(activeMultiDayIdAtom);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const jobId = jobData?.id;
  const jobStudio = jobData?.studio;
  const jobTitle = jobData?.title;
  const isClientOnlyJob = jobData?.clientOnlyMode;

  useEffect(() => {
    if (initialJobData?.id) {
      setActiveMultiDayId(initialJobData.id);
    }

    if (!initialJobData?.extendedProps) return;
    const jobProps = initialJobData.extendedProps;
    setJobData(jobProps);
  }, [initialJobData, setActiveMultiDayId]);

  const [activeTab, setActiveTab] = useState("preProd");
  const [canCloseModal, setCanCloseModal] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSpecialEvent, setIsSpecialEvent] = useState(false);

  useEffect(() => {
    setIsShootTab(eventDataModalOpen);
  }, [eventDataModalOpen, setIsShootTab]);

  useEffect(() => {
    setEmailJobData(jobData);
  }, [jobData, setEmailJobData]);

  useEffect(() => {
    setEmailClientData(jobClientData);
  }, [jobClientData, setEmailClientData]);

  useEffect(() => {
    if (jobData.studio === "Staff") {
      setIsSpecialEvent(true);
    } else if (jobData.studio === "N/A") {
      setIsSpecialEvent(true);
    } else if (jobData.studio === "Critical Events") {
      setIsSpecialEvent(true);
    } else if (jobData.studio === "Post Production") {
      setIsSpecialEvent(true);
    } else if (jobData.studio === "Adam") {
      setIsSpecialEvent(true);
    } else {
      setIsSpecialEvent(false);
    }
  }, [jobData, setIsSpecialEvent]);

  useEffect(() => {
    if (clientOnlyMode) {
      setActiveTab("client");
      return;
    }

    const isKanbanView = window.location.href.includes("kanban");
    if (isKanbanView) {
      setActiveTab("job");
      return;
    }

    if (isSpecialEvent) {
      setActiveTab("preProd");
    } else if (eventDataModalTabValue === 0) {
      setActiveTab(isAdmin ? "preProd" : "job");
    } else if (eventDataModalTabValue === 1) {
      setActiveTab("client");
    } else if (eventDataModalTabValue === 2) {
      setActiveTab("job");
    }
  }, [eventDataModalTabValue, isSpecialEvent, isAdmin, clientOnlyMode]);

  useEffect(() => {
    setCanCloseModal(!hasUnsavedChanges);

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !canCloseModal) {
        e.stopPropagation();
        e.preventDefault();

        if (document.activeElement) {
          document.activeElement.blur();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [canCloseModal]);

  useEffect(() => {
    if (!initialJobData?.id) return;

    if (clientOnlyMode && initialJobData.kind === "client") {
      const unsubscribe = onSnapshot(
        doc(db, "ClientDatabase", initialJobData.id),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = { id: snapshot.id, ...snapshot.data() };
            setJobClientData(data);
            setJobData({
              id: `client-${data.id}`,
              client: data.brand,
              clientOnlyMode: true,
            });
          }
        },
        (error) => {
          console.error("Error getting client data snapshot:", error);
        }
      );

      return () => unsubscribe();
    }

    const jobIdToFetch = activeMultiDayId || initialJobData.id;

    const unsubscribe = onSnapshot(
      doc(db, "JobData", jobIdToFetch),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = { ...snapshot.data() };
          data.id = snapshot.id;
          setJobData(data);
        }
      },
      (error) => {
        console.error("Error getting job data snapshot:", error);
      }
    );

    return () => unsubscribe();
  }, [initialJobData?.id, activeMultiDayId, clientOnlyMode]);

  // Separate ClientDatabase subscription so it's properly cleaned up when the
  // job's client changes (e.g. switching A/B tabs). Previously this was nested
  // inside the JobData callback and leaked a new listener on every tab switch.
  useEffect(() => {
    if (!jobData?.client || clientOnlyMode) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, "ClientDatabase"),
        where("brand", "==", jobData.client),
        limit(1)
      ),
      (querySnapshot) => {
        const clientDocs = [];
        querySnapshot.forEach((doc) => {
          clientDocs.push({ id: doc.id, ...doc.data() });
        });
        setJobClientData(clientDocs.length > 0 ? clientDocs[0] : null);
      },
      (error) => {
        console.error("Error getting client data:", error);
      }
    );

    return () => unsubscribe();
  }, [jobData?.client, clientOnlyMode]);

  useEffect(() => {
    if (!jobId || isClientOnlyJob) return;

    const titleIncludesNa =
      typeof jobTitle === "string" && jobTitle.toLowerCase().includes("n/a");

    if (!titleIncludesNa || jobStudio !== "Staff") {
      return;
    }

    updateDoc(doc(db, "JobData", jobId), { studio: "N/A" })
      .then(() => {
        setJobData((prevJobData) =>
          prevJobData?.id === jobId
            ? {
              ...prevJobData,
              studio: "N/A",
            }
            : prevJobData
        );
      })
      .catch((error) => {
        console.error("Error forcing studio to N/A based on title:", error);
      });
  }, [jobId, jobStudio, jobTitle, isClientOnlyJob]);

  // Subscribe to all days in a multi-day job
  useEffect(() => {
    if (!jobData.id || clientOnlyMode) return;

    const isMultiDay =
      jobData.isMultiDay ||
      jobData.parentJobId ||
      (jobData.multiDayChildren && jobData.multiDayChildren.length > 0);

    if (!isMultiDay) {
      setAllMultipleDayJobs([jobData]);
      return;
    }

    const parentId = jobData.parentJobId || jobData.id;
    let childUnsubscribes = [];

    const parentUnsub = onSnapshot(
      doc(db, "JobData", parentId),
      async (snapshot) => {
        if (snapshot.exists()) {
          const parentData = { ...snapshot.data(), id: snapshot.id };

          childUnsubscribes.forEach((unsub) => unsub());
          childUnsubscribes = [];

          if (
            parentData.multiDayChildren &&
            parentData.multiDayChildren.length > 0
          ) {
            try {
              // Batch fetch all children for complete initial snapshot
              const childDocs = await Promise.all(
                parentData.multiDayChildren.map((childId) =>
                  getDoc(doc(db, "JobData", childId))
                )
              );

              const allJobs = [
                parentData,
                ...childDocs
                  .filter((snap) => snap.exists())
                  .map((snap) => ({ id: snap.id, ...snap.data() })),
              ].sort((a, b) => (a.multiDayOrder || 1) - (b.multiDayOrder || 1));

              setAllMultipleDayJobs(allJobs);

              // Set up real-time subscriptions for each child
              parentData.multiDayChildren.forEach((childId) => {
                const childUnsub = onSnapshot(
                  doc(db, "JobData", childId),
                  (childSnapshot) => {
                    if (childSnapshot.exists()) {
                      const childData = {
                        id: childSnapshot.id,
                        ...childSnapshot.data(),
                      };

                      setAllMultipleDayJobs((prev) => {
                        const filtered = prev.filter(
                          (j) => j.id !== childId && j.id !== parentId
                        );
                        return [parentData, ...filtered, childData].sort(
                          (a, b) =>
                            (a.multiDayOrder || 1) - (b.multiDayOrder || 1)
                        );
                      });
                    }
                  },
                  (error) => {
                    console.error("Error in child subscription:", error);
                  }
                );
                childUnsubscribes.push(childUnsub);
              });
            } catch (error) {
              console.error("Error batch fetching children:", error);
              setAllMultipleDayJobs([parentData]);
            }
          } else {
            setAllMultipleDayJobs([parentData]);
          }
        }
      },
      (error) => {
        console.error("Error fetching parent job:", error);
      }
    );

    return () => {
      parentUnsub();
      childUnsubscribes.forEach((unsub) => unsub());
    };
  }, [jobData.id, jobData.isMultiDay, jobData.parentJobId, clientOnlyMode]);

  const handleTabChange = (_, newValue) => {
    setTimeout(() => {
      setActiveTab(newValue);
    }, 0);
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 1,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <div
      id="modal-div"
      style={{
        overflowY: "auto",
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        paddingLeft: 15,
        paddingRight: 15,
        paddingBottom: 20,
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor:
            theme === "dark"
              ? "rgba(28, 28, 28, 0.95)"
              : "rgba(250, 250, 250, 0.95)",
          backdropFilter: "blur(10px)",
          padding: "40px 0 0 0",
          marginBottom: 2,
          borderRadius: "0px !important",
        }}
      >
        <CloseButton
          setEventDataModalOpen={setEventDataModalOpen}
          canCloseModal={canCloseModal}
        />
        <JobTitle
          jobData={jobData}
          isSpecialEvent={isSpecialEvent}
          clientOnlyMode={clientOnlyMode}
        />
        <MenuButton
          jobData={jobData}
          clientData={jobClientData}
          setEventDataModalOpen={setEventDataModalOpen}
          currentTab={activeTab}
          allMultipleDayJobs={allMultipleDayJobs}
          activeMultiDayId={activeMultiDayId}
          setActiveMultiDayId={setActiveMultiDayId}
          onOpenHistory={
            jobData?.id ? () => setHistoryDialogOpen(true) : null
          }
        />

        {allMultipleDayJobs.length > 1 && !clientOnlyMode && (
          <Box sx={{ width: "100%", mb: 1, mt: 2, px: isMobile ? 0 : 0 }}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { duration: 0.3 } },
              }}
            >
              <Tabs
                value={activeMultiDayId || jobData.id}
                onChange={(_, newValue) => setActiveMultiDayId(newValue)}
                variant="fullWidth"
                sx={{
                  borderRadius: "8px",
                  background:
                    theme === "dark"
                      ? "rgba(50, 50, 50, 0.25)"
                      : "rgba(230, 230, 230, 0.6)",
                  backdropFilter: "blur(10px)",
                  minHeight: "48px",
                  "& .MuiTabs-indicator": {
                    backgroundColor: theme === "dark" ? "#64b5f6" : "#2196f3",
                    height: "2px",
                    borderRadius: "2px 2px 0 0",
                  },
                  "& .MuiTab-root": {
                    minHeight: "48px",
                    padding: "10px 24px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.6)"
                        : "rgba(0, 0, 0, 0.6)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    "&:hover": {
                      color: theme === "dark" ? "#ffffff" : "#000000",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                    },
                    "&.Mui-selected": {
                      color: theme === "dark" ? "#90caf9" : "#1976d2",
                      fontWeight: 700,
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(100, 181, 246, 0.08)"
                          : "rgba(33, 150, 243, 0.1)",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: theme === "dark" ? "#64b5f6" : "#2196f3",
                        borderRadius: "2px 2px 0 0",
                      },
                    },
                  },
                  "& .MuiTabs-scrollButtons": {
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)",
                    "&.Mui-disabled": {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                {allMultipleDayJobs.map((dayJob) => (
                  <Tab
                    key={dayJob.id}
                    label={
                      dayJob.multiDaySuffix || dayJob.multipleDays || "Day"
                    }
                    value={dayJob.id}
                  />
                ))}
              </Tabs>
            </motion.div>
          </Box>
        )}

        {!isSpecialEvent && !clientOnlyMode && (
          <Box
            sx={{
              width: "100%",
              mb: -2,
              mt: 2,
              px: isMobile ? 0 : 0,
            }}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { duration: 0.3 } },
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  borderRadius: "8px",
                  background:
                    theme === "dark"
                      ? "rgba(50, 50, 50, 0.25)"
                      : "rgba(230, 230, 230, 0.6)",
                  backdropFilter: "blur(10px)",
                  minHeight: "48px",
                  "& .MuiTabs-indicator": {
                    backgroundColor: theme === "dark" ? "#b39ddb" : "#9575cd",
                    height: "2px",
                    borderRadius: "2px 2px 0 0",
                  },
                  "& .MuiTab-root": {
                    minHeight: "48px",
                    padding: "10px 24px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.6)"
                        : "rgba(0, 0, 0, 0.6)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    "&:hover": {
                      color: theme === "dark" ? "#ffffff" : "#000000",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                    },
                    "&.Mui-selected": {
                      color: theme === "dark" ? "#d1c4e9" : "#673ab7",
                      fontWeight: 700,
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(179, 157, 219, 0.08)"
                          : "rgba(149, 117, 205, 0.1)",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: theme === "dark" ? "#b39ddb" : "#9575cd",
                        borderRadius: "2px 2px 0 0",
                      },
                    },
                  },
                  "& .MuiTabs-scrollButtons": {
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)",
                    "&.Mui-disabled": {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                {isAdmin && <Tab label="Pre Prod" value="preProd" />}
                <Tab label="Client" value="client" />
                <Tab label="Job" value="job" />
              </Tabs>
            </motion.div>
          </Box>
        )}
      </Paper>

      <Box sx={{ pt: 1 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={tabVariants}
            style={{ width: "100%" }}
          >
            {activeTab === "preProd" && (
              <PreProdTab
                jobData={jobData}
                jobClientData={jobClientData}
                isEventTomorrow={isEventTomorrow}
                setHasUnsavedChanges={setHasUnsavedChanges}
                confirmationStatusEmailOpen={confirmationStatusEmailOpen}
                setConfirmationStatusEmailOpen={setConfirmationStatusEmailOpen}
                firstConfirmationStatusEmailOpen={
                  firstConfirmationStatusEmailOpen
                }
                setFirstConfirmationStatusEmailOpen={
                  setFirstConfirmationStatusEmailOpen
                }
                setConfirmationEmailData={setConfirmationEmailData}
                isSpecialEvent={isSpecialEvent}
                allMultipleDayJobs={allMultipleDayJobs}
              />
            )}

            {activeTab === "client" && (
              <ClientTab
                jobClientData={jobClientData}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
            )}

            {activeTab === "job" && (
              <JobTab
                jobData={jobData}
                setJobData={setJobData}
                clientDataInit={jobClientData}
                setHasUnsavedChanges={setHasUnsavedChanges}
                isEventTomorrow={isEventTomorrow}
                confirmationStatusEmailOpen={confirmationStatusEmailOpen}
                setConfirmationStatusEmailOpen={setConfirmationStatusEmailOpen}
                firstConfirmationStatusEmailOpen={
                  firstConfirmationStatusEmailOpen
                }
                setFirstConfirmationStatusEmailOpen={
                  setFirstConfirmationStatusEmailOpen
                }
                setConfirmationEmailData={setConfirmationEmailData}
                allMultipleDayJobs={allMultipleDayJobs}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>
      <div style={{ height: "120px" }}>&nbsp;</div>

      {/* Email Selector Dialog */}
      <EmailSelectorDialogV2
        open={emailSelectorDialogOpen}
        handleClose={() => setEmailSelectorDialogOpen(false)}
        jobData={jobData}
        clientData={jobClientData}
      />
      <HistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        jobId={jobData?.id || null}
        jobData={jobData}
      />
    </div>
  );
};

export default EventDataModalV2;
